import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt, faUser, faBuilding } from '@fortawesome/free-solid-svg-icons';
import { closingDateBadge } from '../closingDateBadge';
import { clientShort } from '../clientShort';

const DEPT_STYLE = {
  define:    { bg: '#e8f0fe', color: '#1967d2', label: 'Define' },
  voa:       { bg: '#fef7cd', color: '#a16207', label: 'Voa' },
  immersion: { bg: '#fce8e6', color: '#c5221f', label: 'Immersion' },
};

const TYPE_LABEL = {
  contrat: 'Contrat',
  appel_offre: 'AO',
  consultation_restreinte: 'CR',
};

function getDeadlineStatus(project) {
  const field = `${project.step}_deadline`;
  const raw = project[field];
  if (!raw) return { text: null, cls: '' };

  const deadline = new Date(raw);
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const diff = Math.ceil((deadline - now) / 86400000);

  const dateStr = deadline.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
  // Show time for AO deadline (only step with datetime), if time is set
  const hasTime = project.step === 'ao' && (deadline.getHours() !== 0 || deadline.getMinutes() !== 0);
  const text = hasTime
    ? `${dateStr} · ${deadline.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`
    : dateStr;

  if (diff < 0) return { text, cls: 'deadline-overdue' };
  if (diff <= 3) return { text, cls: 'deadline-soon' };
  return { text, cls: 'deadline-normal' };
}

const ProjectCard = ({ project, onClick, isDragOverlay, accentColor = '#757575' }) => {
  const {
    attributes, listeners, setNodeRef, transform, transition, isDragging,
  } = useSortable({
    id: `project-${project.id}`,
    data: { project },
    disabled: isDragOverlay,
  });

  const style = isDragOverlay ? {} : {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.3 : 1,
  };

  const dept = DEPT_STYLE[project.department] || DEPT_STYLE.immersion;
  const dl = getDeadlineStatus(project);
  const total = project.total_slots || 0;
  const finished = project.finished_slots || 0;
  const pct = total > 0 ? Math.round((finished / total) * 100) : 0;
  const isOverdue = dl.cls === 'deadline-overdue';

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={`project-card${isOverdue ? ' overdue' : ''}${isDragOverlay ? ' drag-overlay-card' : ''}`}
      onClick={() => { if (!isDragging) onClick(project.id); }}
    >
      {/* Badges */}
      <div className="project-card-badges">
        <span className="badge-dept" style={{ background: dept.bg, color: dept.color }}>
          {dept.label}
        </span>
        <span className="badge-type">
          {TYPE_LABEL[project.project_type] || project.project_type}
        </span>
        {project.step === 'closing' && (() => {
          const b = closingDateBadge(project, { withStep: true });
          return b ? <span className={b.blink ? 'closing-badge-blink' : ''} style={{ fontSize: '0.6rem', fontWeight: 600, padding: '2px 7px', borderRadius: 3, background: b.bg, color: b.color, marginLeft: 'auto' }}>{b.text}</span> : null;
        })()}
      </div>

      {/* Name + client */}
      <div className="project-card-name">{project.client?.name && <span style={{ color: '#9e9e9e', fontWeight: 600 }}>{clientShort(project.client.name, project.client.abbreviation)} - </span>}{project.name}</div>
      <div className="project-card-client">
        {project.client?.name && <FontAwesomeIcon icon={faBuilding} size="xs" style={{ opacity: 0.35, marginRight: 4 }} />}
        {project.client?.name || '—'}
      </div>

      {/* Progress */}
      {total > 0 && (
        <div className="project-card-progress-section">
          <div className="project-card-progress-header">
            <span className="project-card-progress-label">Progression</span>
            <span className="project-card-progress-value">{finished}/{total}</span>
          </div>
          <div className="project-card-progress-bar">
            <div
              className="project-card-progress-fill"
              style={{
                width: `${pct}%`,
                background: pct === 100 ? '#137333' : accentColor,
              }}
            />
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="project-card-footer">
        <div className="project-card-assignee">
          {((project.assignees && project.assignees.length > 0) || project.assigned_to) && (
            <FontAwesomeIcon icon={faUser} size="xs" style={{ opacity: 0.35, marginRight: 5 }} />
          )}
          {(project.assignees && project.assignees.length > 0)
            ? project.assignees.map(a => a.name).join(', ')
            : (project.assigned_to ? project.assigned_to.name : '')}
        </div>

        {dl.text && (
          <span className={`project-card-deadline ${dl.cls}`}>
            <FontAwesomeIcon icon={faCalendarAlt} size="xs" style={{ opacity: 0.5 }} />
            {dl.text}
          </span>
        )}
      </div>
    </div>
  );
};

export default ProjectCard;
